import React from 'react';
import { Alert, Stack, Typography, Box, Divider } from '@mui/material';
import Grid from '@mui/material/Grid';
import WordCloudChart from './charts/WordCloudChart';
import BarChart from './charts/BarChart';
import StackedBarChart from './charts/StackedBarChart';
import PieChart from './charts/PieChart';
import GaugeChart from './charts/GaugeChart';
import SimpleTable from './charts/SimpleTable';

const QuestionAnswers = ({ data, view, colours, questionType, inputType, columnInputTypes }) => {
    if (questionType === 'multipletext' || questionType === 'matrixdynamic') {
        return (
            <Grid container spacing={2}>
                {data.map((e, i) => {
                    const keyColours = colours.find(c => c.key === e.key)?.colours ?? [];

                    const chartHeight = e.values.length * 50;

                    const columnInputType = columnInputTypes?.find(c => c.title === e.key)?.inputType;

                    return (
                        <Grid key={i} size={6}>
                            <Stack spacing={1}>
                                <Typography variant="subtitle1">
                                    {e.key}:
                                </Typography>
                                {view === 'wordcloud' && (
                                    <WordCloudChart
                                        data={e.values}
                                        colours={keyColours}
                                        inputType={columnInputType || inputType}
                                    />
                                )}
                                {view === 'table' && (
                                    <SimpleTable
                                        data={e.values}
                                        inputType={columnInputType || inputType}
                                    />
                                )}
                                {view === 'bar' && (
                                    <BarChart
                                        data={e.values}
                                        colours={keyColours}
                                        chartHeight={chartHeight}
                                        inputType={columnInputType || inputType}
                                    />
                                )}
                                {view === 'pie' && (
                                    <PieChart
                                        data={e.values}
                                        colours={keyColours}
                                        inputType={columnInputType || inputType}
                                    />
                                )}
                            </Stack>
                        </Grid>
                    );
                })}
            </Grid>
        );
    }

    if (questionType === 'matrixdropdown') {
        if (view === 'bar') {
            return (
                <Grid container spacing={2}>
                    {data.map((e, i) => {
                        const columnInputType = columnInputTypes?.find(c => c.title === e.column)?.inputType;

                        return (
                            <Grid key={i} size={12}>
                                <Typography variant="h6" color="theme.palette.textSecondary">
                                    {e.key}
                                </Typography>
                                <Box>
                                    <Grid container spacing={2}>
                                        {e.values.map((v, i) => {
                                            const choiceColours = v.choices.map(choice => colours.find(colour => colour.choice === choice.name)?.colour || '');

                                            return (
                                                <Grid key={i} size={{ xs: 12, md: 6 }}>
                                                    <BarChart
                                                        data={v.choices}
                                                        colours={choiceColours}
                                                        title={v.row}
                                                        chartHeight={v.choices.length * 50}
                                                        inputType={columnInputType || inputType}
                                                    />
                                                </Grid>
                                            );
                                        })}
                                    </Grid>
                                </Box>
                                {i < data.length - 1 && (
                                    <Divider />
                                )}
                            </Grid>
                        );
                    })}
                </Grid>
            );
        }

        if (view === 'stackedbar') {
            const series = data.map(({ key, values }) => {
                const choiceMap = {};
                const rows = values.map(v => v.row);

                values.forEach((entry, rowIndex) => {
                    entry.choices.forEach(({ name, value }) => {
                        if (!choiceMap[name]) {
                            choiceMap[name] = Array(rows.length).fill(0);
                        }

                        choiceMap[name][rowIndex] += value;
                    });
                });

                const data = Object.entries(choiceMap).map(([name, data]) => ({ name, data }));

                return {
                    column: key,
                    rows,
                    data
                };
            });

            return (
                <Grid container spacing={2}>
                    {series.map((e, i) => {
                        const choiceColours = e.data.map(choice => colours.find(colour => colour.choice === choice.name)?.colour || '');

                        const columnInputType = columnInputTypes?.find(c => c.title === e.column)?.inputType;

                        return (
                            <Grid key={i} size={{ xs: 12, md: 6 }}>
                                <Typography variant="h6" color="theme.palette.textSecondary">
                                    {e.column}
                                </Typography>
                                <StackedBarChart
                                    seriesData={e.data}
                                    rows={e.rows}
                                    colours={choiceColours}
                                    chartHeight={e.rows.length * 100}
                                    inputType={columnInputType || inputType}
                                />
                            </Grid>
                        );
                    })}
                </Grid>
            );
        }
    }

    if (questionType === 'matrix') {
        if (view === 'bar') {
            const grouped = data.reduce((acc, { row, column, value }) => {
                acc[row] = acc[row] || [];
                acc[row].push({ name: column, value });

                return acc;
            }, {});

            const chartData = Object.entries(grouped).map(([key, values]) => ({ key, values }));

            return (
                <Grid container spacing={2}>
                    {chartData.map((e, i) => (
                        <Grid key={i} size={6}>
                            <BarChart
                                data={e.values}
                                colours={colours}
                                title={e.key}
                                chartHeight={e.values.length * 50}
                            />
                        </Grid>
                    ))}
                </Grid>
            );
        }

        if (view === 'stackedbar') {
            // eslint-disable-next-line no-undef
            const rows = [...new Set(data.map(e => e.row))];
            // eslint-disable-next-line no-undef
            const columns = [...new Set(data.map(e => e.column))];

            const series = columns.map(column => ({
                name: column,
                data: rows.map(row => {
                    const matches = data.filter(item => item.row === row && item.column === column);

                    return matches.reduce((sum, item) => sum + item.value, 0);
                })
            }));

            return (
                <StackedBarChart
                    seriesData={series}
                    rows={rows}
                    colours={colours}
                    chartHeight={rows.length * 100}
                />
            );
        }

        return (
            <Alert severity="error">
                Unexpected Type ({view}): Please contact your Account Manager
            </Alert>
        );
    }

    const textColumnName = questionType === 'ranking' ? 'Ranking' : questionType === 'rating' ? 'Rating' : null;

    switch (view) {
        case 'pie':
            return (
                <PieChart
                    data={data}
                    colours={colours}
                    inputType={inputType}
                />
            );
        case 'bar':
            return (
                <BarChart
                    data={data}
                    colours={colours}
                    type={questionType}
                    inputType={inputType}
                />
            );
        case 'stackedbar':
            return (
                <StackedBarChart
                    data={data}
                    colours={colours}
                    inputType={inputType}
                />
            );
        case 'wordcloud':
            return (
                <WordCloudChart
                    data={data}
                    colours={colours}
                    inputType={inputType}
                />
            );
        case 'table':
            return (
                <SimpleTable
                    data={data}
                    inputType={inputType}
                    textColumnName={textColumnName}
                />
            );
        case 'gauge':
            return (
                <GaugeChart
                    data={data}
                />
            );
        default:
            return (
                <Alert severity="error">
                    Unexpected Type ({view}): Please contact your Account Manager
                </Alert>
            );
    }
};

export default QuestionAnswers;